import React, { useState, useEffect } from 'react';
import { Settings, Users, Plus, Edit2, Trash2, Server, Link as LinkIcon, AlertCircle } from 'lucide-react';
import Layout from '../../component/layout';
import ApiConfig from '../../api/ApiConfig';
import {
  getWithAuthCallWithErrorResponse,
  PostCallWithErrorResponse,
  deleteWithAuthCall,
} from '../../api/ApiServices';

// ─── AI Agent API Functions ───────────────────────────────────────────────────

async function fetchAllAiAgents() {
  return getWithAuthCallWithErrorResponse(ApiConfig.GET_ALL_AI_AGENTS);
}

async function createAiAgent(data) {
  return PostCallWithErrorResponse(ApiConfig.CREATE_AI_AGENT, JSON.stringify(data));
}

async function updateAiAgent(data) {
  const resp = await fetch(ApiConfig.UPDATE_AI_AGENT, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + localStorage.getItem('Token'),
    },
    body: JSON.stringify(data),
  });
  return resp.json();
}

async function deleteAiAgent(agentId) {
  return deleteWithAuthCall(`${ApiConfig.DELETE_AI_AGENT}?agentId=${agentId}`);
}

// ─── AI Agent Mapping API Functions ──────────────────────────────────────────

async function fetchAllAiAgentMappings() {
  return getWithAuthCallWithErrorResponse(ApiConfig.GET_ALL_AI_AGENT_MAPPINGS);
}

async function createAiAgentMapping(data) {
  return PostCallWithErrorResponse(ApiConfig.CREATE_AI_AGENT_MAPPING, JSON.stringify(data));
}

async function updateAiAgentMapping(data) {
  const resp = await fetch(ApiConfig.UPDATE_AI_AGENT_MAPPING, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + localStorage.getItem('Token'),
    },
    body: JSON.stringify(data),
  });
  return resp.json();
}

async function deleteAiAgentMapping(id) {
  return deleteWithAuthCall(`${ApiConfig.DELETE_AI_AGENT_MAPPING}?id=${id}`);
}

// ─── Employees by Type API Function ──────────────────────────────────────────

async function fetchEmployeesByType(type = 'AI') {
  return getWithAuthCallWithErrorResponse(`${ApiConfig.GET_EMPLOYEES_BY_TYPE}?type=${type}`);
}

// ─── Component ────────────────────────────────────────────────────────────────

const AiSetting = () => {
  const [activeTab, setActiveTab] = useState('registries');

  const [agents, setAgents] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal States
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form States
  const [agentForm, setAgentForm] = useState({ agentId: '', invokeUrl: '', secretKey: '', aoPs: '', isActive: true });
  const [mappingForm, setMappingForm] = useState({ id: null, employeeId: '', agentId: '', isActive: true });

  // ─── Load Data on Mount ─────────────────────────────────────────────────────

  useEffect(() => {
    loadAgents();
    loadMappings();
    loadEmployees();
  }, []);

  async function loadAgents() {
    setLoading(true);
    try {
      const res = await fetchAllAiAgents();
      if (res?.json?.success) {
        setAgents(res.json.data || []);
      }
    } catch (err) {
      console.error('Failed to load AI agents', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadMappings() {
    try {
      const res = await fetchAllAiAgentMappings();
      if (res?.json?.success) {
        setMappings(res.json.data || []);
      }
    } catch (err) {
      console.error('Failed to load mappings', err);
    }
  }

  async function loadEmployees() {
    try {
      const res = await fetchEmployeesByType('AI');
      if (res?.json?.Success) {
        setEmployees(res.json.Data || []);
      }
    } catch (err) {
      console.error('Failed to load employees', err);
    }
  }

  // ─── Agent Handlers ─────────────────────────────────────────────────────────

  const handleOpenAgentModal = (agent = null) => {
    if (agent) {
      setAgentForm({ ...agent });
      setIsEditing(true);
    } else {
      setAgentForm({ agentId: '', invokeUrl: '', secretKey: '', aoPs: '', isActive: true });
      setIsEditing(false);
    }
    setShowAgentModal(true);
  };

  const handleSaveAgent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        AgentId: agentForm.agentId,
        InvokeUrl: agentForm.invokeUrl,
        SecretKey: agentForm.secretKey,
        AOPs: agentForm.aoPs,
        IsActive: agentForm.isActive,
      };

      if (isEditing) {
        const res = await updateAiAgent(payload);
        if (res?.success) {
          setAgents(agents.map(a => a.agentId === agentForm.agentId ? { ...a, ...agentForm } : a));
        }
      } else {
        const res = await createAiAgent(payload);
        if (res?.json?.success) {
          setAgents([...agents, res.json.data]);
        }
      }
      setShowAgentModal(false);
    } catch (err) {
      console.error('Failed to save agent', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm(`Are you sure you want to delete AI Agent: ${agentId}?`)) return;
    try {
      const res = await deleteAiAgent(agentId);
      if (res?.success) {
        setAgents(agents.filter(a => a.agentId !== agentId));
        setMappings(mappings.filter(m => m.agentId !== agentId));
      }
    } catch (err) {
      console.error('Failed to delete agent', err);
    }
  };

  // ─── Mapping Handlers ───────────────────────────────────────────────────────

  const handleOpenMappingModal = (mapping = null) => {
    if (mapping) {
      setMappingForm({ ...mapping });
      setIsEditing(true);
    } else {
      setMappingForm({ id: 0, employeeId: '', agentId: agents.length > 0 ? agents[0].agentId : '', isActive: true });
      setIsEditing(false);
    }
    setShowMappingModal(true);
  };

  const handleSaveMapping = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        Id: mappingForm.id || 0,
        EmployeeId: parseInt(mappingForm.employeeId),
        AgentId: mappingForm.agentId,
        IsActive: mappingForm.isActive,
      };

      if (isEditing) {
        const res = await updateAiAgentMapping(payload);
        if (res?.success) {
          setMappings(mappings.map(m => m.id === mappingForm.id ? { ...m, ...mappingForm } : m));
        }
      } else {
        const res = await createAiAgentMapping(payload);
        if (res?.json?.success) {
          setMappings([...mappings, res.json.data]);
        }
      }
      setShowMappingModal(false);
    } catch (err) {
      console.error('Failed to save mapping', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMapping = async (id) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) return;
    try {
      const res = await deleteAiAgentMapping(id);
      if (res?.success) {
        setMappings(mappings.filter(m => m.id !== id));
      }
    } catch (err) {
      console.error('Failed to delete mapping', err);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
    <Layout>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');


        :root {
          --theme-primary: #3b82f6;
          --theme-primary-hover: #2563eb;
          --theme-bg: #f8fafc;
          --theme-card-bg: #ffffff;
          --theme-text: #334155;
          --theme-text-muted: #64748b;
          --theme-border: #e2e8f0;
        }

        body {
          font-family: 'Inter', sans-serif;
          background-color: var(--theme-bg);
          color: var(--theme-text);
          -webkit-font-smoothing: antialiased;
        }

        .dashboard-container { max-width: 1200px; margin: 0 auto; padding: 2rem 1rem; }

        .header-title { font-weight: 600; color: #1e293b; font-size: 1.5rem; display: flex; align-items: center; gap: 0.5rem; }
        .header-subtitle { color: var(--theme-text-muted); font-size: 0.875rem; margin-top: 0.25rem; }

        .custom-card {
          background: var(--theme-card-bg);
          border-radius: 12px;
          border: 1px solid var(--theme-border);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
          overflow: hidden;
        }

        .nav-tabs { border-bottom: 1px solid var(--theme-border); padding: 0 1rem; background: var(--theme-card-bg); border-top-left-radius: 12px; border-top-right-radius: 12px; }
        .nav-tabs .nav-link { color: var(--theme-text-muted); border: none; font-weight: 500; padding: 1rem 1.5rem; border-bottom: 2px solid transparent; transition: all 0.2s; display: flex; align-items: center; gap: 0.5rem;}
        .nav-tabs .nav-link:hover { color: var(--theme-primary); }
        .nav-tabs .nav-link.active { color: var(--theme-primary); background: transparent; border-bottom: 2px solid var(--theme-primary); }

        .custom-table { margin-bottom: 0; }
        .custom-table th { background-color: #f8fafc; color: #64748b; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; padding: 1rem; border-bottom: 1px solid var(--theme-border); border-top: none;}
        .custom-table td { padding: 1rem; vertical-align: middle; color: #475569; font-size: 0.875rem; border-bottom: 1px solid var(--theme-border); }
        .custom-table tr:last-child td { border-bottom: none; }

        .btn-theme { background-color: var(--theme-primary); color: white; border: none; border-radius: 8px; font-weight: 500; padding: 0.5rem 1rem; display: flex; align-items: center; gap: 0.375rem; transition: background 0.2s; }
        .btn-theme:hover { background-color: var(--theme-primary-hover); color: white;}
        .btn-theme:disabled { opacity: 0.6; cursor: not-allowed; }

        .btn-icon { background: none; border: none; padding: 0.375rem; border-radius: 6px; color: #94a3b8; transition: all 0.2s; display: inline-flex; align-items: center; justify-content: center; }
        .btn-icon:hover { background: #f1f5f9; color: var(--theme-primary); }
        .btn-icon.delete:hover { background: #fee2e2; color: #ef4444; }

        .status-badge { padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 500; display: inline-flex; align-items: center; justify-content: center;}
        .status-active { background-color: #dcfce7; color: #166534; }
        .status-inactive { background-color: #f1f5f9; color: #64748b; }

        .custom-modal-backdrop { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(2px); z-index: 1040; display: flex; align-items: center; justify-content: center; }
        .custom-modal { background: white; width: 100%; max-width: 500px; border-radius: 12px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); display: flex; flex-direction: column; overflow: hidden; animation: modalIn 0.2s ease-out; }
        @keyframes modalIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

        .modal-header { padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--theme-border); display: flex; justify-content: space-between; align-items: center; }
        .modal-title { font-weight: 600; font-size: 1.125rem; color: #1e293b; margin: 0; }
        .modal-body { padding: 1.5rem; }
        .modal-footer { padding: 1rem 1.5rem; border-top: 1px solid var(--theme-border); background: #f8fafc; display: flex; justify-content: flex-end; gap: 0.75rem; }

        .form-label { font-size: 0.875rem; font-weight: 500; color: #475569; margin-bottom: 0.375rem; }
        .form-control, .form-select { border-radius: 8px; border: 1px solid #cbd5e1; padding: 0.625rem 0.75rem; font-size: 0.875rem; color: #1e293b; transition: all 0.2s;}
        .form-control:focus, .form-select:focus { border-color: var(--theme-primary); box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
        .form-text { font-size: 0.75rem; color: #94a3b8; margin-top: 0.25rem; }

        .code-block { background: #f1f5f9; padding: 0.5rem; border-radius: 6px; font-family: monospace; font-size: 0.75rem; color: #334155; max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      `}</style>

      <div className="dashboard-container">
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <h1 className="header-title"><Settings size={28} color="#3b82f6"/> Settings & Configurations</h1>
            <p className="header-subtitle">Manage external AI integrations and map them to internal employee tasks.</p>
          </div>
        </div>

        <div className="custom-card">
          {/* Navigation Tabs */}
          <ul className="nav nav-tabs" role="tablist">
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'registries' ? 'active' : ''}`} onClick={() => setActiveTab('registries')}>
                <Server size={18} />
                AI Agent Registries
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'mappings' ? 'active' : ''}`} onClick={() => setActiveTab('mappings')}>
                <Users size={18} />
                Employee Mappings
              </button>
            </li>
          </ul>

          {/* Tab Content */}
          <div className="tab-content">

            {/* REGISTRIES TAB */}
            {activeTab === 'registries' && (
              <div className="tab-pane fade show active p-0">
                <div className="p-3 d-flex justify-content-between align-items-center border-bottom border-light">
                  <span className="text-muted small fw-medium px-2">Registered AI Systems</span>
                  <button className="btn-theme" onClick={() => handleOpenAgentModal()}>
                    <Plus size={16} /> Add AI Agent
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="table custom-table table-hover">
                    <thead>
                      <tr>
                        <th>Agent ID</th>
                        <th>Invoke URL</th>
                        <th>AOPs (Procedures)</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan="5" className="text-center py-4 text-muted">Loading...</td></tr>
                      ) : agents.length === 0 ? (
                        <tr><td colSpan="5" className="text-center py-4 text-muted">No AI Agents found.</td></tr>
                      ) : agents.map(agent => (
                        <tr key={agent.agentId}>
                          <td className="fw-medium text-dark">{agent.agentId}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <LinkIcon size={14} className="text-muted" />
                              <span className="text-truncate" style={{maxWidth: '200px', display:'inline-block'}}>{agent.invokeUrl}</span>
                            </div>
                          </td>
                          <td><div className="code-block">{agent.aoPs}</div></td>
                          <td>
                            <span className={`status-badge ${agent.isActive ? 'status-active' : 'status-inactive'}`}>
                              {agent.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="text-end">
                            <button className="btn-icon me-1" onClick={() => handleOpenAgentModal(agent)} title="Edit"><Edit2 size={16} /></button>
                            <button className="btn-icon delete" onClick={() => handleDeleteAgent(agent.agentId)} title="Delete"><Trash2 size={16} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* MAPPINGS TAB */}
            {activeTab === 'mappings' && (
              <div className="tab-pane fade show active p-0">
                <div className="p-3 d-flex justify-content-between align-items-center border-bottom border-light">
                  <span className="text-muted small fw-medium px-2">Employee to Agent Task Routing</span>
                  <button className="btn-theme" onClick={() => handleOpenMappingModal()}>
                    <Plus size={16} /> Create Mapping
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="table custom-table table-hover">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Internal Employee</th>
                        <th>Assigned AI Agent</th>
                        <th>Routing Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappings.length === 0 ? (
                        <tr><td colSpan="5" className="text-center py-4 text-muted">No mappings found.</td></tr>
                      ) : mappings.map(mapping => (
                        <tr key={mapping.id}>
                          <td className="text-muted">#{mapping.id}</td>
                          <td className="fw-medium text-dark">
                            {employees.find(e => e.EmployeeId === mapping.employeeId)?.EmployeeName || `Employee #${mapping.employeeId}`}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                               <Server size={14} className="text-primary" />
                               {mapping.agentId}
                            </div>
                          </td>
                          <td>
                            <span className={`status-badge ${mapping.isActive ? 'status-active' : 'status-inactive'}`}>
                              {mapping.isActive ? 'Active' : 'Paused'}
                            </span>
                          </td>
                          <td className="text-end">
                            <button className="btn-icon me-1" onClick={() => handleOpenMappingModal(mapping)} title="Edit"><Edit2 size={16} /></button>
                            <button className="btn-icon delete" onClick={() => handleDeleteMapping(mapping.id)} title="Delete"><Trash2 size={16} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* --- MODALS --- */}

      {/* AI Agent Modal */}
      {showAgentModal && (
        <div className="custom-modal-backdrop">
          <div className="custom-modal">
            <form onSubmit={handleSaveAgent}>
              <div className="modal-header">
                <h5 className="modal-title">{isEditing ? 'Edit AI Agent' : 'Register AI Agent'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowAgentModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Agent ID</label>
                  <input type="text" className="form-control" required
                    value={agentForm.agentId}
                    onChange={e => setAgentForm({...agentForm, agentId: e.target.value})}
                    readOnly={isEditing}
                    placeholder="e.g., HuskyVoice.AI" />
                  {isEditing && <div className="form-text text-warning"><AlertCircle size={12}/> Agent ID cannot be changed once created.</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Invoke URL (Webhook)</label>
                  <input type="text" className="form-control" required
                    value={agentForm.invokeUrl}
                    onChange={e => setAgentForm({...agentForm, invokeUrl: e.target.value})}
                    placeholder="https://api..." />
                </div>
                <div className="mb-3">
                  <label className="form-label">Secret Key (Optional)</label>
                  <input type="text" className="form-control"
                    value={agentForm.secretKey}
                    onChange={e => setAgentForm({...agentForm, secretKey: e.target.value})}
                    placeholder="Provide API key if required by endpoint" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Agent Operating Procedures (AOPs JSON)</label>
                  <textarea className="form-control" rows="3" required
                    value={agentForm.aoPs}
                    onChange={e => setAgentForm({...agentForm, aoPs: e.target.value})}
                    placeholder='{"role": "Front Desk", "rules": []}' />
                </div>
                <div className="form-check form-switch mt-3">
                  <input className="form-check-input" type="checkbox" role="switch" id="agentActive"
                    checked={agentForm.isActive}
                    onChange={e => setAgentForm({...agentForm, isActive: e.target.checked})} />
                  <label className="form-check-label" htmlFor="agentActive">Agent is Active</label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light border" onClick={() => setShowAgentModal(false)}>Cancel</button>
                <button type="submit" className="btn-theme" disabled={saving}>{saving ? 'Saving...' : 'Save Agent'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AI Mapping Modal */}
      {showMappingModal && (
        <div className="custom-modal-backdrop">
          <div className="custom-modal">
            <form onSubmit={handleSaveMapping}>
              <div className="modal-header">
                <h5 className="modal-title">{isEditing ? 'Edit Task Routing' : 'Map Employee to Agent'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowMappingModal(false)}></button>
              </div>
              <div className="modal-body">
                {isEditing && (
                  <div className="mb-3">
                    <label className="form-label">Mapping ID</label>
                    <input type="text" className="form-control bg-light" readOnly value={mappingForm.id} />
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label">Internal Employee</label>
                  <select className="form-select" required
                    value={mappingForm.employeeId}
                    onChange={e => setMappingForm({...mappingForm, employeeId: parseInt(e.target.value) || ''})}>
                    <option value="" disabled>Select an Employee</option>
                    {employees.map(emp => (
                      <option key={emp.EmployeeId} value={emp.EmployeeId}>{emp.EmployeeName}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Target AI Agent</label>
                  <select className="form-select" required
                    value={mappingForm.agentId}
                    onChange={e => setMappingForm({...mappingForm, agentId: e.target.value})}>
                    <option value="" disabled>Select an Agent</option>
                    {agents.map(a => (
                      <option key={a.agentId} value={a.agentId}>{a.agentId}</option>
                    ))}
                  </select>
                  {agents.length === 0 && <div className="form-text text-danger">Please register an AI Agent first.</div>}
                </div>
                <div className="form-check form-switch mt-3">
                  <input className="form-check-input" type="checkbox" role="switch" id="mappingActive"
                    checked={mappingForm.isActive}
                    onChange={e => setMappingForm({...mappingForm, isActive: e.target.checked})} />
                  <label className="form-check-label" htmlFor="mappingActive">Enable Routing</label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light border" onClick={() => setShowMappingModal(false)}>Cancel</button>
                <button type="submit" className="btn-theme" disabled={agents.length === 0 || saving}>
                  {saving ? 'Saving...' : 'Save Mapping'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </Layout>
    </>
  );
};

export default AiSetting;
